import { allLangs, defaultLang } from 'src/locales';
import { localStorageGetItem } from './storage-available';

const langVariants = {
  en: 'en-US',
  it: 'it-IT',
};

export const getDateStringFormate = () => {
  const langStorage = localStorageGetItem('i18nextLng');

  const currentLang = allLangs.find((lang) => lang.value === langStorage) || defaultLang;

  const currentLangValue: string = currentLang.value;

  const currentDate = new Date();
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Rome',
  };
  // @ts-ignore
  return currentDate.toLocaleDateString(langVariants[currentLangValue], options);
};

const ROME_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Rome',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** NAT-2805 — the Rome wall-clock parts of an instant, whatever zone the browser is in. */
const romeParts = (date: Date) => {
  const parts = Object.fromEntries(ROME_PARTS.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    day: `${parts.day}/${parts.month}/${parts.year}`,
    time: `${parts.hour}:${parts.minute}`,
  };
};

/**
 * NAT-2820 — the one Rome-day helper: an instant's Europe/Rome calendar day as yyyy-MM-dd,
 * whatever zone the browser is in. The backend resolves every "today" on Rome's calendar, so
 * every request day, default and window the app sends is read through this.
 */
export const todayInRome = (now: Date = new Date()): string => {
  const [dd, mm, yyyy] = romeParts(now).day.split('/');
  return `${yyyy}-${mm}-${dd}`;
};

/** The single definition of every export's "esportato il" stamp: dd/MM/yyyy HH:mm, Europe/Rome. */
export const exportTimestamp = (date: Date = new Date()): string => {
  const { day, time } = romeParts(date);
  return `${day} ${time}`;
};

/** The Rome day of an instant as dd/MM/yyyy (an export's "situazione al" when it states no day). */
export const exportDay = (date: Date = new Date()): string => romeParts(date).day;

/** True when year-month-day is a real calendar day (month 1-12, day within the month). */
export const isRealYmd = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

/**
 * An export's reference day: the response's own yyyy-MM-dd Rome day when it states one (printed
 * as it is, no zone conversion), else the Rome day of the export.
 */
export const exportAsOfDay = (ymd: string | null | undefined, now: Date = new Date()): string => {
  const m = ymd ? /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(ymd) : null;
  // An impossible day («2026-13-45») is not a day the response can have meant: say the export's
  // own Rome day instead of printing it as «45/13/2026».
  return m && isRealYmd(Number(m[1]), Number(m[2]), Number(m[3] ?? '1'))
    ? `${m[3] ?? '01'}/${m[2]}/${m[1]}`
    : exportDay(now);
};

/**
 * Returns date in dd-mm-yyyy format for file exports (the Rome day, like the stamp inside).
 */
export const getDateForFileExport = (date: Date = new Date()): string =>
  exportDay(date).replace(/\//g, '-');
